import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/context';
import { INSTITUTIONS, fetchInstitutionHoldings } from '../utils/parse13f';
import { STOCKS } from '../utils/stockFundamentals';
import { buildSectorDistribution, getConcentrationBadge, getTickerForCusip, getTopHoldingsConcentration } from '../utils/portfolioMetrics';
import './InstitutionDetailPage.css';

function getChangeLabel(type, lang) {
  const labels = {
    new: { zh: '新增', en: 'New' },
    increased: { zh: '增持', en: 'Added' },
    decreased: { zh: '减持', en: 'Trimmed' },
    flat: { zh: '持平', en: 'Flat' },
  };
  return labels[type]?.[lang] || labels.flat[lang];
}

function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const width = 360;
  const height = 96;
  const values = points.map((point) => point.totalValue);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const coords = points.map((point, index) => {
    const x = points.length === 1 ? 0 : (index / (points.length - 1)) * width;
    const y = height - ((point.totalValue - min) / range) * (height - 12) - 6;
    return { x, y };
  });
  const path = coords.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ');

  return (
    <svg className="inst-detail-sparkline" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Value trend">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((point, index) => (
        <circle key={index} cx={point.x} cy={point.y} r="3.5" />
      ))}
    </svg>
  );
}

const supportedStockTickers = new Set(STOCKS.map((stock) => stock.ticker));

export default function InstitutionDetailPage() {
  const { cik } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [holdings, setHoldings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const institution = INSTITUTIONS.find(
    (inst) => inst.cik === cik || inst.cik.replace(/^0+/, '').padStart(10, '0') === cik
  );

  useEffect(() => {
    if (!institution) {
      setError(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    fetchInstitutionHoldings(institution, { includeHistory: true })
      .then((data) => {
        setHoldings(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [cik, institution]);

  if (loading) {
    return (
      <div className="inst-detail-page">
        <div className="inst-detail-loading">{t('home.instDetailLoading')}</div>
      </div>
    );
  }

  if (error || !institution || !holdings) {
    return (
      <div className="inst-detail-page">
        <button className="inst-detail-back" onClick={() => navigate(-1)}>{t('home.instDetailBack')}</button>
        <div className="inst-detail-not-found">{t('home.instDetailNotFound')}</div>
      </div>
    );
  }

  const top5 = holdings.holdings.slice(0, 5);
  const visibleHoldings = holdings.holdings.slice(0, 20);
  const concentration = getTopHoldingsConcentration(holdings.holdings);
  const concentrationBadge = getConcentrationBadge(concentration, lang);
  const description = lang === 'zh' ? institution.desc : institution.descEn;
  const sectors = buildSectorDistribution(holdings.holdings, lang).slice(0, 6);
  const changeSummary = holdings.changeSummary;
  const trendPoints = holdings.trend?.points || [];
  const qoqChange = holdings.trend?.qoqChangePct;

  const MANAGERS = {
    '0001067983': 'Warren Buffett',
    '0001166559': 'Ray Dalio',
    '0000040645': 'David Solomon',
    '0001364742': 'Larry Fink',
    '0000031678': 'Mortimer Buckley',
    '0001756111': 'Cathie Wood',
  };
  const manager = MANAGERS[institution.cik] || '';

  return (
    <div className="inst-detail-page">
      <button className="inst-detail-back" onClick={() => navigate(-1)}>{t('home.instDetailBack')}</button>

      {/* 机构头部 */}
      <div className="inst-detail-header">
        <div className="inst-detail-header-icon">🏦</div>
        <div className="inst-detail-header-info">
          <div className="inst-detail-header-name">{institution.nameEn}</div>
          <div className="inst-detail-header-sub">
            {manager && <>{manager} {t('home.instDetailManager')} · </>}
            {t('home.instDetailQuarterly')} · {t('home.instDetailLatestUpdate')}：{holdings.filingDate || '—'}
          </div>
          {description && <div className="inst-detail-header-desc">{description}</div>}
          <div className={`inst-detail-concentration-badge ${concentrationBadge.className}`}>
            {concentrationBadge.label} {concentration.toFixed(1)}%
          </div>
          {changeSummary && (
            <div className="inst-detail-change-summary">
              {changeSummary.new} {lang === 'zh' ? '新增' : 'New'} · {changeSummary.increased} {lang === 'zh' ? '增持' : 'Added'} · {changeSummary.decreased} {lang === 'zh' ? '减持' : 'Trimmed'} · {changeSummary.sold} {lang === 'zh' ? '清仓' : 'Sold'}
            </div>
          )}
        </div>
        <div className="inst-detail-header-value-wrap">
          <div className="inst-detail-header-value">
            ${(holdings.totalValue / 1000).toFixed(1)}B
          </div>
          <div className="inst-detail-header-value-label">{t('home.instDetailTotalValue')}</div>
        </div>
      </div>

      {/* Tab 导航 */}
      <div className="inst-detail-tabs">
        <button
          className={`inst-detail-tab${activeTab === 'overview' ? ' active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >{t('home.instDetailTabOverview')}</button>
        <button
          className={`inst-detail-tab${activeTab === 'history' ? ' active' : ''}`}
          onClick={() => setActiveTab('history')}
        >{t('home.instDetailTabHistory')}</button>
        <button
          className={`inst-detail-tab${activeTab === 'concentration' ? ' active' : ''}`}
          onClick={() => setActiveTab('concentration')}
        >{t('home.instDetailTabConcentration')}</button>
      </div>

      <div className="inst-detail-body">
        <div className="inst-detail-main">
          {activeTab === 'overview' && (
            <>
              {/* 持仓列表表格 */}
              <div className="inst-detail-table-wrap">
                <table className="inst-detail-table">
                  <thead>
                    <tr>
                      <th>{t('home.instDetailStock')}</th>
                      <th>{t('home.instDetailValue')}</th>
                      <th>{t('home.instDetailWeight')}</th>
                      <th>{t('home.instDetailShares')}</th>
                      <th>{lang === 'zh' ? '变动' : 'Change'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleHoldings.map((h, i) => (
                      <tr key={i}>
                        <td className="inst-detail-stock-name">{h.nameOfIssuer}</td>
                        <td>${(h.value / 1000).toFixed(1)}B</td>
                        <td>
                          <div className="inst-detail-weight-bar-wrap">
                            <div
                              className="inst-detail-weight-bar"
                              style={{ width: `${Math.min(parseFloat(h.weight), 100)}%` }}
                            />
                            <span>{h.weight}%</span>
                          </div>
                        </td>
                        <td>{h.shares?.toLocaleString()}</td>
                        <td>
                          <span className={`inst-detail-change-tag ${h.changeType || 'flat'}`}>
                            {getChangeLabel(h.changeType, lang)}
                            {h.shareChangePct != null && ` ${h.shareChangePct >= 0 ? '+' : ''}${h.shareChangePct.toFixed(1)}%`}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeTab === 'concentration' && (
            <div className="inst-detail-card">
              <div className="inst-detail-card-title">{t('home.instDetailConcentration')}</div>
              <div className="inst-detail-concentration-bar">
                {top5.map((h, i) => {
                  const colors = ['#4caf50', '#1a73e8', '#f9a825', '#888', '#5e6ad2'];
                  const w = parseFloat(h.weight);
                  return (
                    <div
                      key={i}
                      className="inst-detail-conc-segment"
                      style={{ width: `${w}%`, background: colors[i % colors.length] }}
                    >
                      {w > 5 && <span>{h.nameOfIssuer.split(' ')[0]} {w}%</span>}
                    </div>
                  );
                })}
                <div className="inst-detail-conc-segment rest" style={{ flex: 1 }} />
              </div>
              <div className="inst-detail-source-note">
                {t('home.instDetailSource')} · {t('home.instDetailSourceNote')}
              </div>
              {sectors.length > 0 && (
                <div className="inst-detail-sector-card">
                  <div className="inst-detail-card-title">{lang === 'zh' ? '行业分布' : 'Sector Mix'}</div>
                  {sectors.map((sector) => (
                    <div key={sector.key} className="inst-detail-sector-row">
                      <span>{sector.label}</span>
                      <div className="inst-detail-sector-bar-wrap">
                        <div className={`inst-detail-sector-bar sector-${sector.key}`} style={{ width: `${Math.min(sector.weight, 100)}%` }} />
                      </div>
                      <strong>{sector.weight.toFixed(1)}%</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="inst-detail-card">
              <div className="inst-detail-card-title">{t('home.instDetailTabHistory')}</div>
              {trendPoints.length >= 2 ? (
                <div className="inst-detail-trend">
                  <div className="inst-detail-trend-head">
                    <span>{lang === 'zh' ? '最近季度市值趋势' : 'Recent filing value trend'}</span>
                    {qoqChange != null && (
                      <strong className={qoqChange >= 0 ? 'positive' : 'negative'}>
                        QoQ {qoqChange >= 0 ? '+' : ''}{qoqChange.toFixed(1)}%
                      </strong>
                    )}
                  </div>
                  <Sparkline points={trendPoints} />
                  <div className="inst-detail-trend-points">
                    {trendPoints.map((point) => (
                      <span key={point.date}>{point.date}: ${(point.totalValue / 1000).toFixed(1)}B</span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="inst-detail-placeholder">
                  {lang === 'zh' ? '历史趋势数据不足' : 'Not enough trend data yet'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 右侧栏 */}
        <div className="inst-detail-sidebar">
          <div className="inst-detail-sidebar-box">
            <div className="inst-detail-sidebar-title">{t('home.instDetailAbout13f')}</div>
            <div className="inst-detail-sidebar-desc">
              {t('home.instDetailAbout13fDesc')}
            </div>
          </div>
          <div className="inst-detail-sidebar-box">
            <div className="inst-detail-sidebar-title">{t('home.instDetailRelatedStocks')}</div>
            <div className="inst-detail-sidebar-links">
              {top5.slice(0, 3).map((h, i) => (
                (() => {
                  const ticker = getTickerForCusip(h.cusip);
                  const label = ticker || h.nameOfIssuer.split(' ')[0];
                  if (ticker && supportedStockTickers.has(ticker)) {
                    return (
                      <button
                        key={i}
                        type="button"
                        className="inst-detail-sidebar-link"
                        onClick={() => navigate(`/stock/${ticker}`)}
                      >
                        {label} {t('home.instDetailDetail')}
                      </button>
                    );
                  }

                  return (
                    <a
                      key={i}
                      className="inst-detail-sidebar-link"
                      href={ticker ? `https://finance.yahoo.com/quote/${ticker}` : `https://finance.yahoo.com/lookup?s=${encodeURIComponent(h.nameOfIssuer)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {label} {t('home.instDetailDetail')}
                    </a>
                  );
                })()
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
